import React, { useState, useEffect } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import SolicitudesService from "../../services/SolicitudesService";
import SucursalesService from "../../services/SucursalesService";
import CajaService from "../../services/CajaService";

import RefreshIcon from "@mui/icons-material/Refresh";
import Icon from "@mdi/react";
import { mdiFileExcel } from "@mdi/js";
import * as XLSX from "xlsx";

import {
  Grid,
  TextField,
  Select,
  MenuItem,
  Paper,
  Typography,
  OutlinedInput,
  Stack,
  Box,
  IconButton,
  Button,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const ReporteFinanciero = () => {
  const getFechaHoy = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const [solicitudes, setSolicitudes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [fechaInicial, setFechaInicial] = useState("");
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState("Todas");
  const [tipoReporte, setTipoReporte] = useState("pagadas"); // "pagadas" o "todas"
  const [fechaDe, setFechaDe] = useState(getFechaHoy());
  const [fechaA, setFechaA] = useState(getFechaHoy());

  useEffect(() => {
    document.title = "Reporte Financiero";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Parser de fechas robusto que soporta YYYY-MM-DD, DD/MM/YYYY, DD-MM-YYYY e ISO timestamps
  const parseFechaDate = (fechaRaw) => {
    if (!fechaRaw) return null;
    if (fechaRaw instanceof Date) {
      return isNaN(fechaRaw.getTime())
        ? null
        : new Date(fechaRaw.getFullYear(), fechaRaw.getMonth(), fechaRaw.getDate());
    }
    const str = String(fechaRaw).trim();
    if (!str) return null;

    const datePart = str.split(" ")[0].split("T")[0];

    // DD/MM/YYYY
    if (datePart.includes("/")) {
      const parts = datePart.split("/");
      if (parts.length === 3) {
        const d = Number(parts[0]);
        const m = Number(parts[1]) - 1;
        const y = Number(parts[2]);
        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
          return new Date(y, m, d);
        }
      }
    }

    // YYYY-MM-DD o DD-MM-YYYY
    if (datePart.includes("-")) {
      const parts = datePart.split("-");
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          // YYYY-MM-DD
          const y = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const d = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        } else if (parts[2].length === 4) {
          // DD-MM-YYYY
          const d = Number(parts[0]);
          const m = Number(parts[1]) - 1;
          const y = Number(parts[2]);
          if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
            return new Date(y, m, d);
          }
        }
      }
    }

    const dt = new Date(str);
    if (!isNaN(dt.getTime())) {
      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    }
    return null;
  };

  const formatDateToYYYYMMDD = (dt) => {
    if (!dt) return "";
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resSol, resSuc, resCaja] = await Promise.all([
        SolicitudesService.getSolicitudes().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
        CajaService.getCaja().catch(() => ({ data: { caja: [] } })),
      ]);

      const listaSol = Array.isArray(resSol?.data?.solicitudes)
        ? resSol.data.solicitudes
        : Array.isArray(resSol?.data)
        ? resSol.data
        : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
        ? resSuc.data
        : [];

      const listaCaja = Array.isArray(resCaja?.data?.caja)
        ? resCaja.data.caja
        : Array.isArray(resCaja?.data)
        ? resCaja.data
        : [];

      const cajaMap = {};
      listaCaja.forEach((c) => {
        if (c.idSolicitud) {
          cajaMap[c.idSolicitud] = c;
        }
      });

      const solicitudesConCaja = listaSol.map((s) => {
        const c = cajaMap[s.idSolicitud];
        return {
          ...s,
          adeudoCaja: c ? Number(c.adeudo ?? 0) : null,
          pagadoCaja: c ? Number(c.pagado ?? 0) : null,
        };
      });

      setSolicitudes(solicitudesConCaja);
      setSucursales(listaSuc);

      // Calcular primera fecha de solicitud registrada en el sistema
      let primeraFecha = getFechaHoy();
      const fechasValidas = solicitudesConCaja
        .map((s) => parseFechaDate(s.fecha || s.fechaCreacion || s.created_at || s.createdAt))
        .filter(Boolean)
        .map((d) => formatDateToYYYYMMDD(d))
        .sort();

      if (fechasValidas.length > 0) {
        primeraFecha = fechasValidas[0];
      }

      setFechaInicial(primeraFecha);
    } catch (error) {
      console.error("Error al cargar datos para Reporte Financiero:", error);
      Swal.fire({
        title: "¡Error!",
        text: "Ocurrió un error al obtener la información del servidor.",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambio en rango de fechas ("De:" o "A:")
  const handleFechaDeChange = (e) => {
    setFechaDe(e.target.value);
    setFechaInicial(""); // Limpiar fecha inicial al cambiar rango
  };

  const handleFechaAChange = (e) => {
    setFechaA(e.target.value);
    setFechaInicial(""); // Limpiar fecha inicial al cambiar rango
  };

  // Restablecer a fecha inicial predeterminada
  const handleRestablecerFiltros = () => {
    let primeraFecha = getFechaHoy();
    const fechasValidas = solicitudes
      .map((s) => parseFechaDate(s.fecha || s.fechaCreacion || s.created_at || s.createdAt))
      .filter(Boolean)
      .map((d) => formatDateToYYYYMMDD(d))
      .sort();

    if (fechasValidas.length > 0) {
      primeraFecha = fechasValidas[0];
    }

    setFechaInicial(primeraFecha);
    setSucursalSeleccionada("Todas");
    setTipoReporte("pagadas");
    setFechaDe(getFechaHoy());
    setFechaA(getFechaHoy());
  };

  // Lógica de filtrado de solicitudes por estatus de pago
  const esSolicitudPagada = (sol) => {
    const estatus = `${sol.estatus ?? ""}`.trim().toUpperCase();
    if (estatus === "C") return false;
    if (estatus === "P" || estatus === "E" || estatus === "EN") return true;
    if (sol.estaPagado === 1 || sol.pagado === 1 || sol.pagado === true) return true;
    if (sol.adeudoCaja !== null && sol.adeudoCaja !== undefined && sol.adeudoCaja === 0) return true;

    const adeudo = Number(sol.saldoPendiente ?? sol.adeudo ?? -1);
    const total = Number(sol.totalPagar ?? sol.total ?? 0);
    if (adeudo === 0 && total > 0) return true;

    return false;
  };

  // Obtener solicitudes que cumplen filtros de TIPO DE REPORTE y RANGO DE FECHA
  const obtenerSolicitudesFiltradasPorFechaYTipo = () => {
    return solicitudes.filter((sol) => {
      // Excluir canceladas
      const estatus = `${sol.estatus ?? ""}`.trim().toUpperCase();
      const isCancelled = estatus === "C" || (sol.motivoCancelacion && sol.motivoCancelacion.trim() !== "");
      if (isCancelled) return false;

      // Filtro de tipo de reporte
      if (tipoReporte === "pagadas" && !esSolicitudPagada(sol)) {
        return false;
      }

      // Filtro de fecha
      const solDate = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.created_at || sol.createdAt);
      if (!solDate) return false;
      const solTime = solDate.getTime();

      if (fechaInicial) {
        // Desde fecha inicial hasta hoy
        const startDate = parseFechaDate(fechaInicial);
        const endDate = parseFechaDate(getFechaHoy());
        if (startDate && endDate) {
          if (solTime < startDate.getTime() || solTime > endDate.getTime()) {
            return false;
          }
        }
      } else {
        // Rango de fechas De: A:
        const startDate = parseFechaDate(fechaDe);
        const endDate = parseFechaDate(fechaA);
        if (startDate && endDate) {
          const minTime = Math.min(startDate.getTime(), endDate.getTime());
          const maxTime = Math.max(startDate.getTime(), endDate.getTime());
          if (solTime < minTime || solTime > maxTime) {
            return false;
          }
        }
      }

      return true;
    });
  };

  const solicitudesFiltradasPeriodo = obtenerSolicitudesFiltradasPorFechaYTipo();

  // Comprobar si una solicitud pertenece a una sucursal específica
  const coincideSucursal = (sol, targetSucId, targetSucNombre) => {
    const idSuc = String(sol.idSucursal ?? sol.sucursalId ?? "");
    const nomSuc = String(sol.nombreSucursal ?? sol.sucursal ?? "").trim().toLowerCase();
    const targetIdStr = String(targetSucId);
    const targetNomStr = String(targetSucNombre).trim().toLowerCase();

    return idSuc === targetIdStr || (nomSuc !== "" && nomSuc === targetNomStr);
  };

  // Construir filas para la tabla de interfaz UI
  const construirFilasTablaUI = () => {
    const fechaTextoUI = fechaInicial ? getFechaHoy() : fechaA || getFechaHoy();

    // Caso 1: Se selecciona una sucursal específica
    if (sucursalSeleccionada !== "Todas") {
      const sucursalObj = sucursales.find(
        (s) => String(s.idSucursal) === String(sucursalSeleccionada) || s.nombre === sucursalSeleccionada
      );
      const nombreSuc = sucursalObj ? sucursalObj.nombre : sucursalSeleccionada;
      const idSuc = sucursalObj ? sucursalObj.idSucursal : sucursalSeleccionada;

      const solSuc = solicitudesFiltradasPeriodo.filter((sol) =>
        coincideSucursal(sol, idSuc, nombreSuc)
      );

      const monto = solSuc.reduce(
        (acc, sol) => acc + Number(sol.totalPagar ?? sol.total ?? sol.monto ?? 0),
        0
      );

      return {
        filas: [
          {
            id: idSuc,
            sucursal: nombreSuc,
            fecha: fechaTextoUI,
            concepto: "Estudios",
            monto: monto,
          },
        ],
        totalGlobal: monto,
        esMultiple: false,
      };
    }

    // Caso 2: Se selecciona "Todas" -> Mostrar todas las sucursales una por una
    // Obtener lista completa de sucursales a mostrar
    let listaSucursalesMostrar = [...sucursales];

    // Si no hay lista de sucursales del endpoint, agrupar por las sucursales presentes en las solicitudes
    if (listaSucursalesMostrar.length === 0) {
      const sucsMap = {};
      solicitudesFiltradasPeriodo.forEach((sol) => {
        const nom = sol.nombreSucursal || sol.sucursal || "Sucursal Principal";
        const id = sol.idSucursal || nom;
        sucsMap[id] = { idSucursal: id, nombre: nom };
      });
      listaSucursalesMostrar = Object.values(sucsMap);
    }

    let totalGlobal = 0;
    const filas = listaSucursalesMostrar.map((suc) => {
      const solSuc = solicitudesFiltradasPeriodo.filter((sol) =>
        coincideSucursal(sol, suc.idSucursal, suc.nombre)
      );

      const monto = solSuc.reduce(
        (acc, sol) => acc + Number(sol.totalPagar ?? sol.total ?? sol.monto ?? 0),
        0
      );

      totalGlobal += monto;

      return {
        id: suc.idSucursal,
        sucursal: suc.nombre,
        fecha: fechaTextoUI,
        concepto: "Estudios",
        monto: monto,
      };
    });

    return {
      filas,
      totalGlobal,
      esMultiple: true,
    };
  };

  const { filas: filasUI, totalGlobal: totalGlobalUI, esMultiple: esMultipleUI } = construirFilasTablaUI();

  // Exportar a Excel (desglose diario)
  const handleExportarExcel = () => {
    // Filtrar solicitudes por sucursal si se seleccionó una específica
    let solicitudesExportar = solicitudesFiltradasPeriodo;
    if (sucursalSeleccionada !== "Todas") {
      const sucursalObj = sucursales.find(
        (s) => String(s.idSucursal) === String(sucursalSeleccionada) || s.nombre === sucursalSeleccionada
      );
      const nombreSuc = sucursalObj ? sucursalObj.nombre : sucursalSeleccionada;
      const idSuc = sucursalObj ? sucursalObj.idSucursal : sucursalSeleccionada;

      solicitudesExportar = solicitudesFiltradasPeriodo.filter((sol) =>
        coincideSucursal(sol, idSuc, nombreSuc)
      );
    }

    if (solicitudesExportar.length === 0) {
      Swal.fire({
        title: "Sin datos",
        text: "No hay registros que coincidan con los filtros aplicados para exportar.",
        icon: "info",
        confirmButtonText: "Aceptar",
      });
      return;
    }

    // Mapa para sucursales por ID
    const mapSucursales = {};
    sucursales.forEach((suc) => {
      mapSucursales[suc.idSucursal] = suc.nombre;
    });

    // Agrupar por sucursal y fecha
    const agrupado = {};

    solicitudesExportar.forEach((sol) => {
      const dt = parseFechaDate(sol.fecha || sol.fechaCreacion || sol.created_at || sol.createdAt);
      if (!dt) return;
      const fechaStr = formatDateToYYYYMMDD(dt);

      const nomSuc =
        sol.nombreSucursal ||
        sol.sucursal ||
        mapSucursales[sol.idSucursal] ||
        "Sucursal Principal";

      const key = `${nomSuc}___${fechaStr}`;

      if (!agrupado[key]) {
        agrupado[key] = {
          sucursal: nomSuc,
          fecha: fechaStr,
          montoTotal: 0,
        };
      }

      const monto = Number(sol.totalPagar ?? sol.total ?? sol.monto ?? 0);
      agrupado[key].montoTotal += isNaN(monto) ? 0 : monto;
    });

    // Convertir agrupado a array de filas para Excel
    const filasExcel = Object.values(agrupado)
      .sort((a, b) => a.fecha.localeCompare(b.fecha) || a.sucursal.localeCompare(b.sucursal))
      .map((item) => ({
        Sucursal: item.sucursal,
        Fecha: item.fecha,
        Concepto: "Estudios",
        Monto: `$${item.montoTotal.toFixed(2)}`,
      }));

    // Crear libro Excel
    const worksheet = XLSX.utils.json_to_sheet(filasExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reporte Financiero");

    // Ajustar anchos de columnas
    worksheet["!cols"] = [
      { wch: 32 }, // Sucursal
      { wch: 16 }, // Fecha
      { wch: 20 }, // Concepto
      { wch: 18 }, // Monto
    ];

    const rangoNombre = fechaInicial
      ? `del_${fechaInicial}_al_${getFechaHoy()}`
      : `del_${fechaDe}_al_${fechaA}`;

    XLSX.writeFile(workbook, `Reporte_Financiero_${rangoNombre}.xlsx`);
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            minHeight: "80vh",
            paddingTop: "2vh",
            paddingBottom: "2vh",
            width: "100%",
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal */}
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    display: "inline-block",
                    whiteSpace: "pre-line",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  REPORTE FINANCIERO
                </Typography>
              </Box>

              {/* Tabla de Filtros (Centrada en el Paper) */}
              <Box sx={{ width: "90%", maxWidth: "1000px", margin: "0 auto", mb: 4 }}>
                <Table
                  sx={{
                    width: "100%",
                    backgroundColor: "white",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    overflow: "hidden",
                    "& .MuiTableCell-root": { padding: "8px 12px" },
                  }}
                >
                  <TableHead sx={{ backgroundColor: "#18234f" }}>
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align="center"
                        sx={{
                          fontSize: "1.2rem",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      >
                        Filtros de Búsqueda
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Fila 1: Fecha inicial y Sucursal */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "20%", backgroundColor: "#f8f9fa" }}>
                        Fecha inicial:
                      </TableCell>
                      <TableCell sx={{ width: "30%" }}>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={fechaInicial}
                          onChange={(e) => setFechaInicial(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          helperText={
                            fechaInicial
                              ? "Mostrando desde primera solicitud"
                              : "Rango personalizado activo"
                          }
                        />
                      </TableCell>

                      <TableCell sx={{ fontWeight: "bold", width: "20%", backgroundColor: "#f8f9fa" }}>
                        Sucursal:
                      </TableCell>
                      <TableCell sx={{ width: "30%" }}>
                        <Select
                          fullWidth
                          size="small"
                          input={<OutlinedInput />}
                          value={sucursalSeleccionada}
                          onChange={(e) => setSucursalSeleccionada(e.target.value)}
                        >
                          <MenuItem value="Todas">Todas</MenuItem>
                          {sucursales.map((suc) => (
                            <MenuItem key={suc.idSucursal} value={suc.idSucursal}>
                              {suc.nombre}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                    </TableRow>

                    {/* Fila 2: Tipo de reporte y Rango de fechas */}
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Tipo de reporte:
                      </TableCell>
                      <TableCell>
                        <Select
                          fullWidth
                          size="small"
                          input={<OutlinedInput />}
                          value={tipoReporte}
                          onChange={(e) => setTipoReporte(e.target.value)}
                        >
                          <MenuItem value="pagadas">
                            Total de solicitudes pagadas en el rango de fechas
                          </MenuItem>
                          <MenuItem value="todas">
                            Total de solicitudes en el rango de fechas
                          </MenuItem>
                        </Select>
                      </TableCell>

                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                        Rango de fechas:
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            De:
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            value={fechaDe}
                            onChange={handleFechaDeChange}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1 }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                            A:
                          </Typography>
                          <TextField
                            type="date"
                            size="small"
                            value={fechaA}
                            onChange={handleFechaAChange}
                            InputLabelProps={{ shrink: true }}
                            sx={{ flex: 1 }}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Fila 3: Botón de reset */}
                    <TableRow>
                      <TableCell colSpan={4} align="right">
                        <Button
                          variant="contained"
                          size="medium"
                          onClick={handleRestablecerFiltros}
                          startIcon={<RefreshIcon />}
                          sx={{
                            backgroundColor: "#18234f",
                            color: "white",
                            "&:hover": { backgroundColor: "#006787" },
                          }}
                        >
                          Restablecer Filtros
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>

              {/* Tabla de Resultados (Centrada en el Paper) */}
              <Box sx={{ width: "90%", maxWidth: "1000px", margin: "0 auto" }}>
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                    <CircularProgress sx={{ color: "#006787" }} />
                  </Box>
                ) : (
                  <Table
                    sx={{
                      width: "100%",
                      backgroundColor: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      overflow: "hidden",
                      "& .MuiTableCell-root": { padding: "12px 16px" },
                    }}
                  >
                    <TableHead sx={{ backgroundColor: "#18234f" }}>
                      <TableRow>
                        <TableCell
                          align="center"
                          sx={{ color: "white", fontWeight: "bold", fontSize: "1vw" }}
                        >
                          Sucursal
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ color: "white", fontWeight: "bold", fontSize: "1vw" }}
                        >
                          Fecha
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ color: "white", fontWeight: "bold", fontSize: "1vw" }}
                        >
                          Concepto
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ color: "white", fontWeight: "bold", fontSize: "1vw" }}
                        >
                          Monto
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filasUI.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            "&:hover": { backgroundColor: "#f5f5f5" },
                          }}
                        >
                          <TableCell align="center" sx={{ fontSize: "1.05vw", fontWeight: "500" }}>
                            {row.sucursal}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: "1.05vw", fontWeight: "500" }}>
                            {row.fecha}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: "1.05vw", fontWeight: "500" }}>
                            {row.concepto}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "1.1vw",
                              fontWeight: "bold",
                              color: "#2e7d32",
                            }}
                          >
                            ${row.monto.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* Fila de Sumatoria Total General si hay múltiples sucursales */}
                      {esMultipleUI && (
                        <TableRow
                          sx={{
                            backgroundColor: "#eef2f6",
                            borderTop: "2px solid #18234f",
                          }}
                        >
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "1.1vw",
                              fontWeight: "bold",
                              color: "#18234f",
                            }}
                          >
                            TOTAL GENERAL
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "1.1vw",
                              fontWeight: "bold",
                              color: "#18234f",
                            }}
                          >
                            {fechaInicial ? getFechaHoy() : fechaA || getFechaHoy()}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "1.1vw",
                              fontWeight: "bold",
                              color: "#18234f",
                            }}
                          >
                            Estudios
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontSize: "1.2vw",
                              fontWeight: "bold",
                              color: "#1b5e20",
                            }}
                          >
                            ${totalGlobalUI.toFixed(2)}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Paper>
          </Stack>
        </Box>

        {/* Botón Lateral Flotante para Exportar a Excel (Con Icono MDI Excel y Hover Grisáseo) */}
        <Box
          sx={{
            position: "fixed",
            right: "2vw",
            top: "30%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          <Tooltip
            title="Exportar a Excel (Desglose Diario)"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarExcel}
              sx={{
                backgroundColor: "#ffffff",
                color: "#107c41",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFileExcel} size={1.4} color="#107c41" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default ReporteFinanciero;